/**
 * Who has not paid — the manual-billing triage queue (phase 3, decision 3).
 *
 * ── Why this is beside the dunning queue and not merged into it ──────────
 *
 * They answer the same question about different worlds and neither can speak for
 * the other. `DunningPanel` reads Stripe's own state (`past_due`, disputes, grace
 * windows) and its rows resolve when Stripe resolves them — nothing on our side
 * acts. These rows come from a date a person typed, and they resolve only when a
 * person acts. Merging them would produce one list whose rows mean two different
 * things, with a "next step" sentence that is wrong for half of them.
 *
 * On any one deployment only one of the two has rows, because `plan_authority`
 * decides which world it is in.
 *
 * ── Every row is ELIGIBLE, never in-progress ────────────────────────────
 *
 * Decision 3 keeps a human in the loop and decision 11 removed the grace timer on
 * the grounds that the operator's judgement IS the grace. So there is no dismiss,
 * no snooze, and no "actioned" state — each would be a second, invisible fact for
 * an operator to reconcile against the date. A row leaves when the date moves or
 * the customer is moved to the fallback plan.
 *
 * Empty is the GOOD state and says so; an empty region is indistinguishable from
 * a failed load. And "nobody is overdue" is a different statement from "this
 * deployment does not bill this way", which is why the mode is reported too.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast, useConfirm, useRefreshWhenShown } from '@papan/core-ui/ui'
import { formatPlatformDateLocal } from '@papan/types'
import {
  budgetApi, type ManualBillingQueueResponse, type ManualBillingOverdueRow,
} from '../../client/budget'

// Platform-shelf queue → Malaysia date order (en-MY), LOCAL time: `paidThrough`
// is a `23:59:59`-local instant, not a midnight-UTC civil day.
const fmtDate = (iso: string | null) => formatPlatformDateLocal(iso)

export function ManualBillingQueue({ onOpenCustomer, canEdit }: {
  /** The queue answers "who"; the customer page answers "and then what". Every
   *  row links into it rather than being a dead label. */
  onOpenCustomer?: (tenantId: string) => void
  /** `customers:write` — the fallback lever. A `member` seat holds
   *  `operations:write` and may WORK this queue but not re-plan a customer, which
   *  is why the button's gate is not the destination's. */
  canEdit: boolean
}) {
  const { t } = useTranslation('core-ui')
  const [data, setData] = useState<ManualBillingQueueResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await budgetApi.getManualBillingOverdue())
      setFailed(false)
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // The shelf/settings deck stays MOUNTED behind `display: none`, so the mount
  // effect above is the only fetch this page would ever make — it would serve
  // whatever was true when the deck was first opened, with no error and no way
  // back but a full reload. See `DeckVisibility`.
  useRefreshWhenShown(() => { void load() })

  // Not in manual mode: say so once, and do not render an empty list that would
  // read as "nobody has lapsed".
  const notManual = !!data && data.planAuthority !== 'manual'

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between gap-4 mb-1">
        <h2 className="themed-text text-lg font-medium flex items-center gap-2">
          {t('settings.platform.manualQueue.title', { defaultValue: 'Unpaid tenants' })}
          {data && !notManual && data.overdue.length > 0 && (
            <span data-toc-ignore className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-medium tabular-nums">
              {data.overdue.length}{data.truncated ? '+' : ''}
            </span>
          )}
        </h2>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="text-xs themed-text-muted hover:themed-text flex items-center gap-1 transition-colors disabled:opacity-60 disabled:cursor-default"
        >
          {/* No icon: `@papan/orgs` deliberately does not depend on
              lucide-react, and its sibling panels in this directory say the same.
              The word carries it. */}
          {t('settings.platform.manualQueue.refresh', { defaultValue: 'Refresh' })}
        </button>
      </div>
      <p className="themed-text-muted text-sm mb-4">
        {t('settings.platform.manualQueue.subtitle', {
          defaultValue:
            'Tenants whose paid-through date has passed. Nothing acts on these — they are listed so a person can decide, which is what manual billing means.',
        })}
      </p>

      {failed && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2">
          <p className="text-amber-300 text-xs font-medium">
            {t('settings.platform.manualQueue.failed', { defaultValue: 'The unpaid queue could not be read.' })}
          </p>
          <p className="themed-text-secondary text-xs mt-0.5">
            {t('settings.platform.manualQueue.failedBody', {
              defaultValue: 'This is not a claim that nobody is overdue.',
            })}
          </p>
        </div>
      )}

      {notManual && (
        <p className="themed-text-secondary text-sm">
          {t('settings.platform.manualQueue.notManual', {
            defaultValue:
              'This deployment\'s plans come from the payment provider, so nothing is tracked here — payment problems appear above instead.',
          })}
        </p>
      )}

      {data && !notManual && (
        <>
          {/* Plans meaningless: the queue is real but acting on it would move a
              plan that changes no limit. Worth saying before an operator clicks. */}
          {!data.plansAreMeaningful && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 mb-3">
              <p className="text-amber-300 text-xs font-medium">
                {t('settings.platform.manualQueue.notMeaningful', {
                  defaultValue: 'Plans are not applying any limits on this deployment.',
                })}
              </p>
              <p className="themed-text-secondary text-xs mt-0.5">
                {t('settings.platform.manualQueue.notMeaningfulBody', {
                  defaultValue: 'Moving a tenant to the fallback plan would change its name and nothing else.',
                })}
              </p>
            </div>
          )}

          {!data.fallbackPlan && data.overdue.length > 0 && (
            <div className="rounded-lg border themed-border px-3 py-2 mb-3">
              <p className="themed-text text-xs font-medium">
                {t('settings.platform.manualQueue.noFallback', {
                  defaultValue: 'No non-payment fallback plan is configured.',
                })}
              </p>
              <p className="themed-text-secondary text-xs mt-0.5">
                {t('settings.platform.manualQueue.noFallbackBody', {
                  defaultValue: 'Choose one in Platform settings — until then there is no lever on these rows.',
                })}
              </p>
            </div>
          )}

          {/* RULE 4 — a restore that did not finish.
              ────────────────────────────────────────────────────────────
              A customer carrying saved ceilings while NOT on the plan those were
              saved for has PAID and may still be on the fallback's limits. It
              ranks above the queue itself: an overdue customer is waiting to be
              chased, this one is being wronged. Named, not counted — a count
              says something is broken without saying who. */}
          {(data.stuckSnapshots?.length ?? 0) > 0 && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 mb-3">
              <p className="text-amber-200 text-xs font-medium">
                {t('settings.platform.manualQueue.stuckTitle', {
                  defaultValue: 'A restore did not finish',
                  count: data.stuckSnapshots!.length,
                })}
              </p>
              <p className="text-amber-200/80 text-xs mt-0.5">
                {t('settings.platform.manualQueue.stuckBody', {
                  defaultValue:
                    'These tenants still have ceilings saved from a fallback they are no longer on. They may be running on the fallback\'s limits after paying. Open each one and restore or discard.',
                })}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {data.stuckSnapshots!.map(sn => (
                  <button
                    key={sn.tenantId}
                    // The host may not supply navigation (the mobile shell does
                    // not). Naming the customer still beats a count, so the
                    // chip degrades to a label rather than disappearing.
                    onClick={onOpenCustomer ? () => onOpenCustomer(sn.tenantId) : undefined}
                    disabled={!onOpenCustomer}
                    className="text-[11px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 enabled:hover:bg-amber-500/30 disabled:cursor-default"
                  >
                    {sn.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!loading && data.overdue.length === 0 && (
            // Empty is the GOOD state and says so in words — an empty region is
            // indistinguishable from a failed load.
            <p className="themed-text-secondary text-sm">
              {t('settings.platform.manualQueue.empty', { defaultValue: 'Every tracked tenant is paid up.' })}
            </p>
          )}

          {/* An EMPTY queue is ambiguous without this: it reads as "everyone has
              paid" when it may mean "nothing is tracked yet". Untracked is a
              SETUP state, not a triage state, so it names the script rather than
              offering a per-customer click. */}
          {(data.untracked ?? 0) > 0 && (
            <p className="themed-text-muted text-xs mt-2">
              {t('settings.platform.manualQueue.untracked', {
                defaultValue:
                  '{{count}} organization has no paid-through date, so it can never appear here. Run `pnpm billing:bootstrap-paid-through` to set them from their history.',
                defaultValue_other:
                  '{{count}} organizations have no paid-through date, so they can never appear here. Run `pnpm billing:bootstrap-paid-through` to set them from their history.',
                count: data.untracked,
              })}
            </p>
          )}

          {data.overdue.length > 0 && (
            <div className="space-y-2" data-testid="manual-billing-queue">
              {data.overdue.map(row => (
                <OverdueCard
                  key={row.tenantId}
                  row={row}
                  fallback={data.fallbackPlan}
                  canEdit={canEdit}
                  onOpenCustomer={onOpenCustomer}
                  onActed={load}
                />
              ))}
            </div>
          )}

          {/* A capped list that did not say it was capped would read as "that is
              everyone", and an operator would stop working before the queue was
              empty. */}
          {data.truncated && (
            <p className="themed-text-muted text-xs mt-2">
              {t('settings.platform.manualQueue.truncated', {
                defaultValue: 'Showing the {{n}} longest-overdue tenants. Work these and refresh for more.',
                n: data.limit,
              })}
            </p>
          )}
        </>
      )}
    </div>
  )
}

function OverdueCard({ row, fallback, canEdit, onOpenCustomer, onActed }: {
  row: ManualBillingOverdueRow
  fallback: ManualBillingQueueResponse['fallbackPlan']
  canEdit: boolean
  onOpenCustomer?: (tenantId: string) => void
  onActed: () => void
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const confirm = useConfirm()
  const [busy, setBusy] = useState(false)

  const days = row.daysOverdue ?? 0
  // A month late is a different conversation from three days late, and the queue
  // is worked by severity even though it is ordered by date.
  const tone = days >= 30 ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'
  const alreadyOnFallback = !!fallback && row.planId === fallback.id

  const apply = async () => {
    if (!fallback) return
    const ok = await confirm({
      title: t('settings.platform.manualQueue.confirmTitle', { defaultValue: 'Move to the fallback plan?' }),
      message: fallback.freezesUploads
        ? t('settings.platform.manualQueue.confirmFreeze', {
            defaultValue:
              '{{name}} will be moved to {{plan}}, which PAUSES new uploads. They keep full read access and can still edit existing files. This takes effect immediately.',
            name: row.name, plan: fallback.name ?? fallback.id,
          })
        : t('settings.platform.manualQueue.confirmPlain', {
            defaultValue:
              '{{name}} will be moved to {{plan}} and inherit all of its limits. This takes effect immediately.',
            name: row.name, plan: fallback.name ?? fallback.id,
          }),
      confirmText: t('settings.platform.manual.fallbackConfirmOk', { defaultValue: 'Apply fallback' }),
      type: 'danger',
    })
    if (!ok) return

    setBusy(true)
    try {
      const r = await budgetApi.applyManualBillingFallback(row.tenantId)
      toast.success(r.unchanged
        ? t('settings.platform.manual.fallbackUnchanged', {
            defaultValue: 'Already on {{plan}} — nothing changed.', plan: r.planName,
          })
        : t('settings.platform.manual.fallbackApplied', {
            defaultValue: 'Moved to {{plan}}.', plan: r.planName,
          }))
      onActed()
    } catch (e: any) {
      toast.error(e?.message || 'Could not apply the fallback plan')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="themed-bg-secondary rounded-lg p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            {onOpenCustomer ? (
              <button
                onClick={() => onOpenCustomer(row.tenantId)}
                className="themed-text text-sm font-medium truncate hover:underline"
              >
                {row.name}
              </button>
            ) : (
              <span className="themed-text text-sm font-medium truncate">{row.name}</span>
            )}
            <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${tone}`}>
              {days === 0
                ? t('settings.platform.manualQueue.overdueToday', { defaultValue: 'Overdue today' })
                : t('settings.platform.manualQueue.overdueDays', { defaultValue: '{{n}} days overdue', n: days })}
            </span>
            {/* Already frozen — so this row is a customer waiting to PAY, not one
                waiting to be acted on. Without it an operator re-applies the
                fallback to the same rows every morning. */}
            {row.uploadsPaused && (
              <span className="text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap bg-red-500/20 text-red-300">
                {t('settings.platform.manualQueue.paused', { defaultValue: 'Uploads paused' })}
              </span>
            )}
          </div>
          <p className="themed-text-muted text-xs mt-0.5 truncate tabular-nums">
            {row.planName ?? '—'}
            {row.planSource === 'override' && (
              <span className="ml-1">
                ({t('settings.platform.customer.planSetHere', { defaultValue: 'set here' })})
              </span>
            )}
            {' · '}
            {t('settings.platform.manualQueue.paidThrough', {
              defaultValue: 'Paid through {{date}}', date: fmtDate(row.paidThrough),
            })}
          </p>
        </div>

        {canEdit && fallback && !alreadyOnFallback && (
          <button
            onClick={apply}
            disabled={busy}
            className="shrink-0 px-2.5 py-1 text-xs rounded border border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 disabled:opacity-50"
          >
            {t('settings.platform.manual.applyFallback', {
              defaultValue: 'Move to {{plan}}', plan: fallback.name ?? fallback.id,
            })}
          </button>
        )}

        {/* The lever used to be simply ABSENT once a tenant was already on the
            fallback, which on a queue where every other row has a red button
            reads as "this row is broken" rather than "this one is done". Saying
            so is also the more useful fact: it is why they are still listed —
            moving them again would do nothing, the debt is what is outstanding. */}
        {canEdit && fallback && alreadyOnFallback && (
          <span className="shrink-0 px-2.5 py-1 text-xs rounded themed-bg-tertiary themed-text-muted">
            {t('settings.platform.manualQueue.alreadyOnFallback', {
              defaultValue: 'Already on {{plan}}', plan: fallback.name ?? fallback.id,
            })}
          </span>
        )}

        {/* Working this queue is `operations` but the fallback move is
            `customers:write`. A seat that can reach the queue yet holds only
            `customers:read` — the read-only viewer/guest tier — sees the button
            ABSENT, not disabled. (The write-tier roles owner/admin/member all
            hold `customers:write` and see it.) Absent-with-no-reason reads as
            "this row is broken"; saying why turns it into an understood
            boundary. */}
        {!canEdit && fallback && !alreadyOnFallback && (
          <span
            className="shrink-0 px-2.5 py-1 text-xs rounded themed-bg-tertiary themed-text-muted"
            title={t('settings.platform.manualQueue.needsCustomersHint', {
              defaultValue: 'The Customers permission is required to move a tenant to the fallback plan.',
            })}
          >
            {t('settings.platform.manualQueue.needsCustomers', {
              defaultValue: 'Needs Customers permission',
            })}
          </span>
        )}
      </div>

      {row.note && (
        // The note is the whole reason this queue is not just a query: it carries
        // the previous operator's judgement ("chasing finance, do not freeze until
        // the 20th"), which is exactly what decision 11 replaced a grace timer with.
        <p className="themed-text-secondary text-xs mt-2 whitespace-pre-wrap break-words">{row.note}</p>
      )}
    </div>
  )
}
